import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from '../static/styles/GameChallenges.module.css';
import { fetchGameChallenges } from '../api/challenges';
import { submitGameEntry } from '../api/games';
import GamingTaskSubmitPopup from '../components/GamingTaskSubmitPopup';
import aimTrainLevels from '../static/data/aimTrainLevels.json';
import puzzleManiaLevels from '../static/data/puzzleManiaLevels.json';
import spaceFootballLevels from '../static/data/spaceFootballLevels.json';
import aimTrainCover from '../static/img/MadeByESL/coverAimTrain.avif';
import puzzleManiaCover from '../static/img/MadeByESL/coverPuzzleMania.avif';
import spaceFootballCover from '../static/img/MadeByESL/coverSpaceFootball.png.avif';
import firstTaskBG from '../static/img/DailyTasks/iconFirstTaskBG.svg';
import submitBtnBg from '../static/img/DailyTasks/iconSubmitEntryBtn.svg';
import dummyImg from '../static/img/Home/pubgDummy.avif';
import { isAuthenticated } from '../api/auth';
import MessagePopup from '../components/MessagePopup';

const GameChallenges = () => {
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('gameId');
  const navigate = useNavigate();

  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [popupImageSrc, setPopupImageSrc] = useState(null);

  const [isSubmitPopupOpen, setIsSubmitPopupOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const [messagePopupOpen, setMessagePopupOpen] = useState(false);
  const [messagePopupMsg, setMessagePopupMsg] = useState('');

  const handleImageClick = (imageUrl) => {
    setPopupImageSrc(imageUrl);
  };

  const handleClosePopup = () => {
    setPopupImageSrc(null);
    // console.log("Closing popup"); 
  };

  const handleOpenSubmitPopup = (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    setSelectedChallenge(challenge);
    setIsSubmitPopupOpen(true);
  };

  const handleCloseSubmitPopup = () => {
    setIsSubmitPopupOpen(false);
    setSelectedChallenge(null);
  };

  // For Aim Train, handle Play Now button
  const handlePlayNow = async (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    const payload = {
      game_unique_id: 'aim_train_001',
      game_name: 'Aim Train',
      game_level: String(challenge.challengeId),
      level_credits: String(challenge.prize)
    };
    try {
      const resp = await submitGameEntry(payload);
      if (resp.success) {
        navigate('/aim-train', { state: payload });
      } else {
        setMessagePopupMsg(resp.message || 'Something went wrong.');
        setMessagePopupOpen(true);
      }
    } catch (e) {
      setMessagePopupMsg('An error occurred. Please try again.');
      setMessagePopupOpen(true);
    }
  };

  // For Puzzle Mania, handle Play Now button
  const handlePlayPuzzleMania = async (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    const payload = {
      game_unique_id: 'puzzle_mania_001',
      game_name: 'Puzzle Mania',
      game_level: String(challenge.challengeId),
      level_credits: String(challenge.prize)
    };
    try {
      const resp = await submitGameEntry(payload);
      if (resp.success) {
        navigate('/puzzle-mania', { state: payload });
      } else {
        setMessagePopupMsg(resp.message || 'Something went wrong.');
        setMessagePopupOpen(true);
      }
    } catch (e) {
      setMessagePopupMsg('An error occurred. Please try again.');
      setMessagePopupOpen(true);
    }
  };

  // For Space Football, handle Play Now button
  const handlePlaySpaceFootball = async (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    const payload = {
      game_unique_id: 'space_football_001',
      game_name: 'Space Football',
      game_level: String(challenge.challengeId),
      level_credits: String(challenge.prize)
    };
    try {
      const resp = await submitGameEntry(payload);
      if (resp.success) {
        navigate('/space-football', { state: payload });
      } else {
        setMessagePopupMsg(resp.message || 'Something went wrong.');
        setMessagePopupOpen(true);
      }
    } catch (e) {
      setMessagePopupMsg('An error occurred. Please try again.');
      setMessagePopupOpen(true);
    }
  };

  useEffect(() => {
    const loadChallenges = async () => {
      if (!gameId) {
        setError('No game selected. Please go back and select a game.');
        setLoading(false);
        return;
      }
      // If AimTrain, skip API and use static data
      if (gameId === 'aim_train_001') {
        setGameData({
          game: 'Aim Train',
          challenges: []
        });
        setLoading(false);
        return;
      }
      // If Puzzle Mania, skip API and use static data
      if (gameId === 'puzzle_mania_001') {
        setGameData({
          game: 'Puzzle Mania',
          challenges: []
        });
        setLoading(false);
        return;
      }
      // If Space Football, skip API and use static data
      if (gameId === 'space_football_001') {
        setGameData({
          game: 'Space Football',
          challenges: []
        });
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchGameChallenges(gameId);
        if (data) {
          setGameData(data);
        } else {
          setError('Failed to load challenges for this game. Please try again later.');
        }
      } catch (err) {
        setError('An error occurred while fetching challenges.');
      } finally {
        setLoading(false);
      }
    };

    loadChallenges();
  }, [gameId]);

  useEffect(() => {
    document.title = 'Game Challenges | Ufone ESG';
  }, []);

  // If Aim Train, use levels from aimTrainLevels.json
  let filteredChallenges = gameData?.challenges
    ?.filter(challenge => challenge.archived === 0)
    ?.sort((a, b) => a.makeEntry - b.makeEntry) || [];

  const isAimTrain = gameId === 'aim_train_001';
  const isPuzzleMania = gameId === 'puzzle_mania_001';
  const isSpaceFootball = gameId === 'space_football_001';
  
  const eslGames = ['aim_train_001', 'puzzle_mania_001', 'space_football_001'];

  if (isAimTrain) {
    filteredChallenges = aimTrainLevels.map(level => ({
      challengeName: `Pass Level ${level.level}`,
      prize: level.reward,
      challengeThumbnail: aimTrainCover,
      makeEntry: 0,
      archived: 0,
      challengeId: level.level,
      id: level.level
    }));
  }

  if (isPuzzleMania) {
    filteredChallenges = puzzleManiaLevels.map(level => ({
      challengeName: `Play ${level.difficulty} Mode`,
      objective: level.description,
      prize: level.reward,
      challengeThumbnail: puzzleManiaCover,
      makeEntry: 0,
      archived: 0,
      challengeId: level.level,
      id: level.level
    }));
  }

  if (isSpaceFootball) {
    filteredChallenges = spaceFootballLevels.map(level => ({
      challengeName: `Beat Level ${level.level}`,
      objective: level.description,
      prize: level.reward,
      challengeThumbnail: spaceFootballCover,
      makeEntry: 0,
      archived: 0,
      challengeId: level.level,
      id: level.level
    }));
  }

  if (loading) {
    return <div className={styles.loadingState}>Loading challenges...</div>;
  }

  if (error) {
    return <div className={styles.errorState}>{error}</div>;
  }

  if (!gameData || filteredChallenges.length === 0) {
    const gameTitle = gameData?.game || 'Selected Game';
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>{gameTitle} Daily Tasks</h2>
        <p className={styles.subheading}>Play daily tasks & win extra credits</p>
        <div className={styles.emptyState}>No challenges available for this game right now.</div>
      </div>
    );
  }

  const { game } = gameData;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>{game} Gaming Tasks</h2>
      <p className={styles.subheading}>Play daily tasks & win extra credits</p>

      <div className={styles.tasksContainer}>
        {filteredChallenges.map((challenge, index) => {
          let imageUrl = challenge.challengeThumbnail;
          
          // Only format base64 for non-ESL games (regular challenges)
          if (!eslGames.includes(gameId) && imageUrl) {
            if (!imageUrl.startsWith('data:') && !imageUrl.startsWith('http')) {
              imageUrl = `data:image/jpeg;base64,${imageUrl}`;
            }
          }
          return (
            <div
              key={challenge.id || index}
              className={styles.taskCard}
              style={index === 0 ? { paddingTop: '35px', backgroundImage: `url(${firstTaskBG})`, backgroundSize: 'cover', border: 'none', borderLeft: '4px solid white' } : {}}
            >
              <div className={styles.taskText}>
                <p className={styles.taskTitle}>{challenge.challengeName || 'Challenge Name'}</p>
                <p className={styles.taskObjective}>{challenge.objective || ''}</p>
                <p className={styles.taskReward}>Win {challenge.prize || 'Reward Info'} Credits</p>
              </div>

              <img
                src={imageUrl}
                alt={challenge.challengeName || 'Challenge'}
                className={styles.taskImage}
                onClick={() => handleImageClick(imageUrl)}
                onError={(e) => { e.target.onerror = null; e.target.src = dummyImg }}
              />

              {challenge.makeEntry === 1 ? (
                <div className={styles.claimed}>CLAIMED</div>
              ) : (
                isAimTrain ? (
                  <div
                    className={styles.submitBtnWrapper}
                    onClick={() => handlePlayNow(challenge)}
                  >
                    <img src={submitBtnBg} alt="Play Now" className={styles.submitBtn} />
                    <span className={styles.submitText}>Play Now</span>
                  </div>
                ) : isPuzzleMania ? (
                  <div
                    className={styles.submitBtnWrapper}
                    onClick={() => handlePlayPuzzleMania(challenge)}
                  >
                    <img src={submitBtnBg} alt="Play Now" className={styles.submitBtn} />
                    <span className={styles.submitText}>Play Now</span>
                  </div>
                ) : isSpaceFootball ? (
                  <div
                    className={styles.submitBtnWrapper}
                    onClick={() => handlePlaySpaceFootball(challenge)}
                  >
                    <img src={submitBtnBg} alt="Play Now" className={styles.submitBtn} />
                    <span className={styles.submitText}>Play Now</span>
                  </div>
                ) : (
                  <div
                    className={styles.submitBtnWrapper}
                    onClick={() => handleOpenSubmitPopup(challenge)}
                  >
                    <img src={submitBtnBg} alt="Submit Entry" className={styles.submitBtn} />
                    <span className={styles.submitText}>Submit Entry</span>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>

      {popupImageSrc && (
        <div className={styles.imagePopupOverlay} onClick={handleClosePopup}>
          <div className={styles.imagePopupContent} onClick={(e) => e.stopPropagation()}>
            <img src={popupImageSrc} alt="Challenge Preview" />
            <button className={styles.closePopupButton} onClick={handleClosePopup}>&times;</button>
          </div>
        </div>
      )}

      {isSubmitPopupOpen && selectedChallenge && (
        <GamingTaskSubmitPopup
          isOpen={isSubmitPopupOpen}
          onClose={handleCloseSubmitPopup}
          gameId={gameId}
          challengeId={selectedChallenge.challengeId}
          taskImage={(() => {
            let taskImage = selectedChallenge.challengeThumbnail;
            // Only format base64 for non-ESL games
            if (!eslGames.includes(gameId) && taskImage) {
              if (!taskImage.startsWith('data:') && !taskImage.startsWith('http')) {
                taskImage = `data:image/jpeg;base64,${taskImage}`;
              }
            }
            return taskImage;
          })()}
        />
      )}
      <MessagePopup
        isOpen={messagePopupOpen}
        message={messagePopupMsg}
        onClose={() => setMessagePopupOpen(false)}
      />
    </div>
  );
};

export default GameChallenges;